// Opens the JiveBird app from the mobile website, or reports that it isn't
// installed so the site can show an install popup.
//
// App paths (handled by the app's deep-link router):
//   home            -> app landing screen
//   create          -> JiveBird builder
//   card/<occasion> -> card picker for that occasion, e.g. card/thank-you

export const APP_STORE_URL = 'https://apps.apple.com/app/id946644356';
export const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.greetings.jivebird';

const APP_SCHEME = 'jivebird';
const ANDROID_PACKAGE = 'com.greetings.jivebird';
const INSTALL_HASH = '#jb-install-app';
const APP_OPEN_TIMEOUT_MS = 2000;

export function getMobilePlatform() {
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  // iPadOS reports itself as a Mac, so also check for touch support
  if (
    /iPhone|iPad|iPod/i.test(ua) ||
    (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)
  ) {
    return 'ios';
  }
  return null;
}

function buildAndroidIntentUrl(path) {
  // If the app isn't installed Chrome navigates to the fallback URL: this same
  // page with INSTALL_HASH, which only fires a hashchange (no reload).
  const fallback = window.location.href.split('#')[0] + INSTALL_HASH;
  return (
    `intent://${path}#Intent;scheme=${APP_SCHEME};package=${ANDROID_PACKAGE};` +
    `S.browser_fallback_url=${encodeURIComponent(fallback)};end`
  );
}

/**
 * Tries to open `path` in the app.
 * Calls `onNotInstalled(platform)` when the app didn't open.
 * Returns false on desktop so the caller can keep its normal web behaviour.
 */
export function openInApp(path, { onNotInstalled }) {
  const platform = getMobilePlatform();
  if (!platform) return false;

  let done = false;
  let timer;

  const cleanup = () => {
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    window.removeEventListener('pagehide', onAppOpened);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('hashchange', onHashChange);
  };
  const finish = installed => {
    if (done) return;
    done = true;
    cleanup();
    if (!installed) onNotInstalled(platform);
  };

  // The browser was backgrounded, so the app opened
  const onAppOpened = () => finish(true);
  const onVisibilityChange = () => {
    if (document.hidden) onAppOpened();
  };
  // iOS shows a system dialog ("Open in JiveBird?" or "address is invalid"),
  // which blurs the page. Wait for the page to come back before deciding.
  const onBlur = () => clearTimeout(timer);
  const onFocus = () => {
    clearTimeout(timer);
    timer = setTimeout(() => finish(document.hidden), 800);
  };
  // Android Chrome intent fallback landed back on this page
  const onHashChange = () => {
    if (window.location.hash === INSTALL_HASH) {
      window.history.replaceState(null, '', window.location.href.split('#')[0]);
      finish(false);
    }
  };

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onAppOpened);
  window.addEventListener('blur', onBlur);
  window.addEventListener('focus', onFocus);
  window.addEventListener('hashchange', onHashChange);
  timer = setTimeout(() => finish(document.hidden), APP_OPEN_TIMEOUT_MS);

  window.location.href =
    platform === 'android'
      ? buildAndroidIntentUrl(path)
      : `${APP_SCHEME}://${path}`;
  return true;
}
